import { InternationalAddress } from './international-address.model';
import { UpsRateBreakdown } from './ups-rate-breakdown.model';
import { UpsPackageStatus, UpsShipmentStatus } from '../enums/ups-shipment-status.enum';

/**
 * Domain types for building a UPS shipment. These are intentionally UPS-field-name
 * free (City, not StateProvinceCode etc.) so the rest of the app deals in clean
 * data; buildUpsShipmentRequest() maps them into UPS's actual ShipmentRequest shape.
 */

/** A structured US address — the parts UPS requires, parsed from a free-text block. */
export interface UpsAddress {
    /** Street lines (UPS allows up to 3). */
    addressLines: string[];
    city: string;
    /** Two-letter state/province code, e.g. "WI". */
    stateProvinceCode: string;
    postalCode: string;
    /** ISO country code; defaults to "US". */
    countryCode: string;
}

/** One parcel/box on a UPS shipment. Dimensions optional. */
export interface UpsPackageInput {
    weightLbs: string;
    lengthIn?: string;
    widthIn?: string;
    heightIn?: string;
    /** Declared (insured) value in USD, e.g. "250.00". Omitted/0 = none. UPS adds the
     *  DeclaredValue/insurance charge to the rate when present. */
    declaredValue?: string;
    /** Up to 2 reference numbers (e.g. PO/SO) printed on THIS package's label. UPS carries
     *  references per-package, so each box can be labeled independently. */
    referenceNumbers?: string[];
}

/** A shipping party (shipper / ship-from / ship-to). */
export interface UpsParty {
    name: string;
    attentionName?: string;
    phone?: string;
    /** Contact email — emitted as EMailAddress when present. */
    email?: string;
    /** Shipper only — the UPS account number (Shipper Number). */
    shipperNumber?: string;
    address: UpsAddress;
}

/** Who the transportation charges bill to. */
export type UpsBillToType = 'shipper' | 'receiver' | 'thirdParty';

/** Billing selection. Receiver/third-party require an account number. */
export interface UpsBilling {
    type: UpsBillToType;
    /** Required for receiver / third-party. */
    accountNumber?: string;
    /** Third-party (and intl receiver) bill-to address. */
    postalCode?: string;
    countryCode?: string;
}

/** Clean input to buildUpsShipmentRequest(): domain data, no UPS field names. */
export interface UpsShipmentInput {
    /** Must include shipperNumber. */
    shipper: UpsParty;
    /** Defaults to the shipper when omitted. */
    shipFrom?: UpsParty;
    shipTo: UpsParty;
    /** UPS service code — "03" = Ground. */
    serviceCode: string;
    serviceDescription?: string;
    packages: UpsPackageInput[];
    description?: string;
    residential?: boolean;
    /** Up to 2 reference numbers (e.g. SO/PO) printed on the label. */
    referenceNumbers?: string[];
    /** Who pays transportation. Defaults to billing the shipper's account. */
    billing?: UpsBilling;
    /** Quantum View Notify recipients — UPS emails each at their selected events. */
    notifyRecipients?: UpsNotifyRecipientInput[];
}

/** A Quantum View Notify recipient for the request builder (UPS-field-name free). */
export interface UpsNotifyRecipientInput {
    email: string;
    onShip: boolean;
    onException: boolean;
    onDelivery: boolean;
}

// ===========================================================================
// Persisted UPS shipment entity (the `ups-shipment` Appwrite collection). Lives in
// prism-common so any department can create one. Stores the full request + results
// (tracking number, label, cost) so we have a history we can view and reprint.
// ===========================================================================

/** A shipping party on a persisted UPS shipment — structured address + contact. */
export interface UpsShipmentParty {
    company: string;
    address: InternationalAddress;
    contactName: string;
    phone: string;
    email: string;
    /** Marks the ship-to as residential (affects UPS rating). */
    residential: boolean;
}

/** One package on a persisted UPS shipment. */
export interface UpsShipmentPackage {
    weightLbs: string;
    lengthIn: string;
    widthIn: string;
    heightIn: string;
    /** UPS packaging type code (e.g. '02' = customer package). */
    packageType: string;
    reference1: string;
    reference2: string;
    /** Declared (insured) value in USD for this package, e.g. "250.00". '' = none. */
    packageValue: string;
    // --- Results, populated once processed (each package has its OWN tracking # + label). ---
    /** UPS tracking number for THIS package. '' until processed. */
    trackingNumber: string;
    /** Raw ZPL label for THIS package, kept so it can be reprinted without re-calling UPS. '' until processed. */
    labelZpl: string;
    /** Progress of THIS package, set by the tracking job from the UPS Tracking API. '' until processed.
     *  When it's Exception, exceptionDetail holds the reason. */
    status: UpsPackageStatus | '';
    /** UPS's exception description for THIS package (e.g. "Address Correction Required"), stored so the
     *  user can triage in-app without opening UPS. '' when there's no active exception. */
    exceptionDetail: string;
}

/** Billing selection on a persisted UPS shipment. */
export interface UpsShipmentBilling {
    type: UpsBillToType;
    accountNumber: string;
    postalCode: string;
    countryCode: string;
    /** Human-readable name of the third-party bill-to account holder (display only — NOT sent to
     *  UPS). '' for shipper/receiver and for records created before this field existed. */
    accountName?: string;
}

/**
 * One UPS Quantum View Notify recipient — UPS emails this address at the selected events.
 * Like WorldShip, a shipment can have several. Maps to ShipmentServiceOptions.Notification
 * (codes: 6 ship, 7 exception, 8 delivery), one entry per enabled event for this email.
 */
export interface UpsShipmentNotifyRecipient {
    id: string;
    email: string;
    onShip: boolean;
    onException: boolean;
    onDelivery: boolean;
}

/**
 * A persisted UPS shipment. Department-agnostic: optionally links to a customer
 * (`customerId` '' when standalone/drop-ship). Departments are distinguished by which
 * UPS account they bill to, so the originating department isn't tracked here.
 */
export interface UpsShipment {
    id: string;
    status: UpsShipmentStatus;
    /** Void & clone lineage. A cloned shipment's `supersedesId` points back to the shipment it
     *  replaced, so from the newest you can back-track predecessor by predecessor to the original and
     *  compare what changed. '' when the shipment isn't part of a clone chain. */
    supersedesId: string;
    /** Linked customer, or '' for a standalone/drop-ship shipment. */
    customerId: string;
    /** Selected customer address id / contact id (existing-tab) — used to preselect the
     *  dropdowns when re-opening the shipment. '' for drop-ship / no contact. */
    addressId: string;
    contactId: string;

    /** Ship-from existing-tab selection (customer / address / contact ids) — used to preselect
     *  the ship-from dropdowns when re-opening. All '' when ship-from was typed (new tab) or left
     *  to default to the shipper account. */
    shipFromCustomerId: string;
    shipFromAddressId: string;
    shipFromContactId: string;

    /** Residential-delivery flags. UPS-only rating concern with no home on `CustomerAddress`
     *  (a reusable address isn't inherently residential — this shipment's delivery is), so they
     *  live flat on the shipment. Source of truth once the party snapshots are gone. */
    shipToResidential: boolean;
    shipFromResidential: boolean;

    /** Company UPS account (Shipper Number) the shipment is sent under. */
    shipperNumber: string;
    /** In-memory working parties, NOT persisted (see appwrite-document-conversion — neither is in
     *  the ups-shipment field list). The builder assembles them from the form for rating + the
     *  unsaved-draft preview, and read surfaces rebuild them from the referenced address/contact
     *  docs via resolveUpsParty. A freshly-loaded shipment carries no party until it's resolved. */
    shipFrom: UpsShipmentParty;
    shipTo: UpsShipmentParty;

    packages: UpsShipmentPackage[];
    serviceCode: string;
    serviceDescription: string;
    billing: UpsShipmentBilling;
    referenceNumbers: string[];
    /** Quantum View Notify recipients (email notifications). Empty = none. */
    notifyRecipients: UpsShipmentNotifyRecipient[];

    // --- Results, populated once processed (empty on a Draft). Per-package tracking numbers
    //     and labels live on each entry in `packages` (a shipment has one per package). ---
    /** UPS ShipmentIdentificationNumber for the whole shipment (equals the lead package's tracking
     *  number). Stored explicitly so tracking/void never depend on packages[0] or array order.
     *  '' until processed. */
    shipmentIdentificationNumber: string;
    /** Published and negotiated total charge, formatted for display (e.g. "$12.34"). */
    publishedCost: string;
    negotiatedCost: string;
    /** Full itemized breakdowns (transportation, surcharges, taxes, total) so the detail
     *  can be revisited/exported. `negotiated` may be empty when none was returned. */
    publishedBreakdown: UpsRateBreakdown;
    negotiatedBreakdown: UpsRateBreakdown;
    processedOn: Date | null;
    /** Last time tracking was checked against UPS (by the cron OR the on-open refresh) — feeds the
     *  browse page's "checked X ago" and throttles the on-open refresh. Distinct from updatedOn
     *  (human edits / delivery flip). null until the first check. */
    lastTrackedOn: Date | null;

    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    updatedById: string;
}

/**
 * The shipment's exception indicator, derived from its packages (any package currently in an
 * Exception state). Not stored — computed wherever the ⚠ indicator is shown, so it can never drift
 * from the packages and needs no column of its own.
 */
export function shipmentHasException(packages: UpsShipmentPackage[]): boolean {
    return (packages ?? []).some(p => p.status === UpsPackageStatus.Exception);
}
